import { useMemo, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Phone,
  MessageCircle,
  Mic,
  Search,
  Clock,
  User,
  FileText,
  Calendar,
  Activity,
  ChevronUp,
  ChevronDown,
  Star,
  Check,
  X,
  Target,
  MoreVertical,
  Trash2,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneForwarded,
} from "lucide-react";
import { sessionApi, type Session } from "../services/sessionApi";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/Loader";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  useMutation,
} from "@tanstack/react-query";
import { transcriptApi } from "@/services/transcriptApi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";

export function SessionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [directionFilter, setDirectionFilter] = useState<string>("all");
  const [analysisFilter, setAnalysisFilter] = useState<string>("all");
  const [satisfactionFilter, setSatisfactionFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"createdAt" | null>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const effectiveSortBy = sortBy || "createdAt";
  const effectiveSortOrder = sortBy ? sortOrder : "desc";

  const {
    data: sessionsData,
    isLoading,
    isFetching,
    error,
  } = useQuery<Session[], unknown>({
    queryKey: [
      "sessions",
      { sortBy: effectiveSortBy, sortOrder: effectiveSortOrder },
    ],
    queryFn: () =>
      sessionApi.getSessions({
        sortBy: effectiveSortBy,
        sortOrder: effectiveSortOrder,
      }),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const sessions = sessionsData ?? [];

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => sessionApi.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      toast.success("Session deleted successfully", {
        position: "bottom-right",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete session", {
        position: "bottom-right",
      });
    },
  });

  const handleDeleteSession = async (sessionId: string) => {
    await deleteSessionMutation.mutateAsync(sessionId).catch(() => undefined);
  };

  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return "Failed to load sessions";
  }, [error]);

  const filteredSessions = useMemo(() => {
    let filtered = sessions;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (session) =>
          session.fromNumber?.toLowerCase().includes(q) ||
          session.user?.firstName?.toLowerCase().includes(q) ||
          session.user?.lastName?.toLowerCase().includes(q) ||
          session.user?.email?.toLowerCase().includes(q) ||
          session.id.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((session) => session.status === statusFilter);
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter((session) => session.type === typeFilter);
    }

    if (directionFilter !== "all") {
      filtered = filtered.filter((session) => session.direction === directionFilter);
    }

    if (analysisFilter !== "all") {
      if (analysisFilter === "analyzed") {
        filtered = filtered.filter((session) => session.analysis !== undefined);
      } else if (analysisFilter === "high_score") {
        filtered = filtered.filter(
          (session) => session.analysis && session.analysis.overallScore >= 80
        );
      } else if (analysisFilter === "low_score") {
        filtered = filtered.filter(
          (session) => session.analysis && session.analysis.overallScore < 60
        );
      } else if (analysisFilter === "not_analyzed") {
        filtered = filtered.filter((session) => session.analysis === undefined);
      }
    }

    if (satisfactionFilter !== "all") {
      if (satisfactionFilter === "satisfied") {
        filtered = filtered.filter(
          (session) => session.analysis?.customerSatisfied === true
        );
      } else if (satisfactionFilter === "unsatisfied") {
        filtered = filtered.filter(
          (session) => session.analysis?.customerSatisfied === false
        );
      }
    }

    return filtered;
  }, [
    sessions,
    searchTerm,
    statusFilter,
    typeFilter,
    directionFilter,
    analysisFilter,
    satisfactionFilter,
  ]);

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case "call":
        return <Phone className="h-4 w-4" />;
      case "web_voice":
        return <Mic className="h-4 w-4" />;
      case "web_chat":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      ACTIVE:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      COMPLETED:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      CANCELLED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
        }
      >
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (milliseconds?: number) => {
    if (!milliseconds) return "-";
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
  };

  const handleSortByCreated = () => {
    if (sortBy === "createdAt") {
      // If already sorting by created date, toggle order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // If not sorting by created date, start with descending order
      setSortBy("createdAt");
      setSortOrder("desc");
    }
  };

  const getSortIcon = () => {
    if (sortBy !== "createdAt") return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const columns = useMemo<ColumnDef<Session>[]>(() => {
    return [
      {
        id: "createdAt",
        header: () => (
          <div
            className={`cursor-pointer  select-none ${
              isFetching && !isLoading ? "pointer-events-none opacity-50" : ""
            }`}
            onClick={handleSortByCreated}
          >
            <div className="flex items-center space-x-1">
              <span>Created</span>
              {getSortIcon()}
            </div>
          </div>
        ),
        cell: ({ row }) => {
          const created = row.original.createdAt;
          return (
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3 text-gray-400" />
              <div>
                <div>{formatDate(created)}</div>
                <div className="text-xs text-gray-500">
                  {formatDateTime(created).split(",")[1]}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "company",
        header: () => <span>Company</span>,
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <span>{row.original.company?.name}</span>
          </div>
        ),
      },
      {
        id: "type",
        header: () => <span>Type</span>,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <div className="flex items-center space-x-2">
              {getSessionTypeIcon(row.original.type)}
              <span>{row.original.type.replace("_", " ")}</span>
            </div>
            {row.original.direction && (
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                {row.original.direction === "outbound" ? (
                  <PhoneOutgoing className="h-3 w-3" />
                ) : (
                  <PhoneIncoming className="h-3 w-3" />
                )}
                <span>{row.original.direction}</span>
                {row.original.callTransfers && row.original.callTransfers.length > 0 && (
                  <>
                    <PhoneForwarded className="h-3 w-3 ml-1 text-orange-500" />
                    <span className="text-orange-500">transferred</span>
                  </>
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "status",
        header: () => <span>Status</span>,
        cell: ({ row }) => getStatusBadge(row.original.status),
      },
      {
        id: "userPhone",
        header: () => <span>User/Phone</span>,
        cell: ({ row }) => {
          const s = row.original;
          return s.user ? (
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-400" />
              <div>
                <div className="font-medium">
                  {s.user.firstName} {s.user.lastName}
                </div>
                <div className="text-sm text-gray-500">{s.user.email}</div>
              </div>
            </div>
          ) : s.fromNumber ? (
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-gray-400" />
              <span>
                {s.fromNumber} to {s.toNumber?.e164Number}
              </span>
            </div>
          ) : (
            <span>-</span>
          );
        },
      },
      {
        id: "duration",
        header: () => <span>Duration</span>,
        cell: ({ row }) => (
          <div className="flex items-center space-x-1">
            <Clock className="h-3 w-3 text-gray-400" />
            <span>{formatDuration(row.original.duration)}</span>
          </div>
        ),
      },
      {
        id: "qualityScore",
        header: () => <span>Quality</span>,
        cell: ({ row }) => {
          const score = row.original.analysis?.overallScore;
          if (score === undefined) {
            return <span className="text-xs text-gray-400">-</span>;
          }

          const getColor = () => {
            if (score >= 80) return "text-green-600";
            if (score >= 60) return "text-yellow-600";
            return "text-red-600";
          };

          return (
            <div className="flex items-center space-x-1">
              <Star className={`h-3 w-3 ${getColor()}`} />
              <span className={`text-sm font-medium ${getColor()}`}>
                {score}
              </span>
            </div>
          );
        },
      },
      {
        id: "satisfaction",
        header: () => <span>Satisfied</span>,
        cell: ({ row }) => {
          const satisfied = row.original.analysis?.customerSatisfied;
          if (satisfied === undefined) {
            return <span className="text-xs text-gray-400">-</span>;
          }
          return satisfied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <X className="h-4 w-4 text-red-600" />
          );
        },
      },
      {
        id: "objective",
        header: () => <span>Objective</span>,
        cell: ({ row }) => {
          const achieved = row.original.analysis?.objectiveAchieved;
          if (achieved === undefined) {
            return <span className="text-xs text-gray-400">-</span>;
          }
          return achieved ? (
            <Target className="h-4 w-4 text-green-600" />
          ) : (
            <Target className="h-4 w-4 text-orange-600" />
          );
        },
      },
      {
        id: "transcripts",
        header: () => <span>Transcripts</span>,
        cell: ({ row }) => (
          <div className="flex items-center space-x-1">
            <FileText className="h-3 w-3 text-gray-400" />
            <span>{row.original._count?.transcripts || 0}</span>
          </div>
        ),
      },
      {
        id: "actions",
        header: () => <span className="text-right w-full block">Actions</span>,
        cell: ({ row }) => {
          const session = row.original;
          return (
            <div className="flex items-center justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span className="sr-only">Open menu</span>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                        }}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Session
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Session</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this session? This
                          will permanently delete the session and all its
                          transcripts. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteSession(session.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [isFetching, isLoading, sortBy, sortOrder]);

  const table = useReactTable({
    data: filteredSessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">All Sessions</h1>
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {errorMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle>Sessions ({filteredSessions.length})</CardTitle>
              {/* Search */}
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search sessions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center space-x-3 flex-wrap gap-2">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="call">Phone Calls</SelectItem>
                  <SelectItem value="web_voice">Web Voice</SelectItem>
                  <SelectItem value="web_chat">Web Chat</SelectItem>
                </SelectContent>
              </Select>

              {/* Direction Filter */}
              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Directions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Directions</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>

              {/* Analysis Filter */}
              <Select value={analysisFilter} onValueChange={setAnalysisFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Analysis Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sessions</SelectItem>
                  <SelectItem value="analyzed">Analyzed</SelectItem>
                  <SelectItem value="not_analyzed">Not Analyzed</SelectItem>
                  <SelectItem value="high_score">High Score (≥80)</SelectItem>
                  <SelectItem value="low_score">Low Score (&lt;60)</SelectItem>
                </SelectContent>
              </Select>

              {/* Satisfaction Filter */}
              <Select
                value={satisfactionFilter}
                onValueChange={setSatisfactionFilter}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Satisfaction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="satisfied">Satisfied</SelectItem>
                  <SelectItem value="unsatisfied">Unsatisfied</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {(statusFilter !== "all" ||
                typeFilter !== "all" ||
                directionFilter !== "all" ||
                analysisFilter !== "all" ||
                satisfactionFilter !== "all" ||
                searchTerm) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setTypeFilter("all");
                    setDirectionFilter("all");
                    setAnalysisFilter("all");
                    setSatisfactionFilter("all");
                    setSearchTerm("");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Sorting...</span>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center py-8 text-gray-500"
                    >
                      {searchTerm ||
                      statusFilter !== "all" ||
                      typeFilter !== "all" ||
                      directionFilter !== "all"
                        ? "No sessions found matching your filters."
                        : "No sessions found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={(e) => {
                        // Don't navigate if clicking on the actions dropdown
                        const target = e.target as HTMLElement;
                        if (
                          target.closest("[data-slot='dropdown-menu']") ||
                          target.closest("[role='dialog']")
                        ) {
                          return;
                        }
                        const session = row.original as Session;
                        navigate(`/admin/sessions/${session.id}`);
                      }}
                      onMouseEnter={() => {
                        const session = row.original as Session;
                        queryClient.prefetchQuery({
                          queryKey: ["session", session.id],
                          queryFn: () => sessionApi.getSession(session.id),
                          staleTime: 60 * 1000,
                        });
                        queryClient.prefetchQuery({
                          queryKey: ["sessionTranscripts", session.id],
                          queryFn: () =>
                            transcriptApi.getTranscriptsBySession(session.id),
                          staleTime: 30 * 1000,
                        });
                      }}
                      className="cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          onClick={(e) => {
                            // Prevent row navigation when clicking on actions cell
                            if (cell.column.id === "actions") {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
